// src/routes/index.tsx
import { lazy } from "react";
import { Route, Routes as RouterRoutes, Navigate, useParams } from "react-router-dom";
import { PrivateRoute } from "./private-route";
import { ProtectedLayout } from "@/components/layout/navigation";
import { RequireFeature } from "@/components/common/require-feature";
import { useAuth } from "@/hooks/use-auth";
import { LoadingPage } from "@/components/common/loading";
import { FEATURE, PERMISSION, REINSURANCE_DEPT } from "@/lib/permissions";
import NotAuthorized from "@/pages/not-authorized";

function RedirectTreatyRulesToDetails() {
  const { id } = useParams();
  return <Navigate to={`/re-insurance/treaty-details/${id}`} replace />;
}

function ReinsuranceHomeRedirect() {
  const { isLoading, hasPermission } = useAuth();
  if (isLoading) return <LoadingPage />;
  if (hasPermission(REINSURANCE_DEPT, FEATURE.TREATY, PERMISSION.VIEW_TREATIES)) {
    return <Navigate to="/re-insurance/treaty" replace />;
  }
  if (hasPermission(REINSURANCE_DEPT, FEATURE.BROKER, PERMISSION.VIEW_BROKERS)) {
    return <Navigate to="/re-insurance/brokers" replace />;
  }
  if (hasPermission(REINSURANCE_DEPT, FEATURE.REINSURER, PERMISSION.VIEW_REINSURERS)) {
    return <Navigate to="/re-insurance/reinsurers" replace />;
  }
  return <NotAuthorized />;
}

// Lazy loaded components
const Treaty = lazy(() => import("@/pages/re-insurance/treaty"));
const TreatyDetails = lazy(
  () => import("@/pages/re-insurance/treaty/treaty-details")
);

const AddTreaty = lazy(() => import("@/pages/re-insurance/treaty/add-treaty"));
const Brokers = lazy(() => import("@/pages/re-insurance/brokers"));
const BrokerDetails = lazy(
  () => import("@/pages/re-insurance/brokers/broker-details")
);
const AddBroker = lazy(() => import("@/pages/re-insurance/brokers/add-broker"));
const EditBroker = lazy(
  () => import("@/pages/re-insurance/brokers/broker-details/edit-broker")
);
const Reinsurers = lazy(() => import("@/pages/re-insurance/reinsurers"));
const ReinsurerDetails = lazy(
  () => import("@/pages/re-insurance/reinsurers/reinsurer-details")
);
const StatementsOverview = lazy(
  () =>
    import(
      "@/pages/re-insurance/reinsurers/reinsurer-details/statements-overview"
    )
);
const AddReinsurer = lazy(
  () => import("@/pages/re-insurance/reinsurers/add-reinsurer")
);
const Compliance = lazy(() => import("@/pages/re-insurance/compliance"));
const Policy = lazy(() => import("@/pages/re-insurance/policy"));
const PolicyDetails = lazy(
  () => import("@/pages/re-insurance/policy/policy-details")
);
const ComplianceTreatyDetails = lazy(
  () => import("@/pages/re-insurance/compliance/treaty-details")
);
const ComplianceBrokerDetails = lazy(
  () => import("@/pages/re-insurance/compliance/broker-details")
);
const ComplianceReinsurerDetails = lazy(
  () => import("@/pages/re-insurance/compliance/reinsurer-details")
);
const RenewTreaty = lazy(
  () => import("@/pages/re-insurance/treaty/treaty-details/renew-treaty")
);
const EditReinsurer = lazy(
  () =>
    import("@/pages/re-insurance/reinsurers/reinsurer-details/edit-reinsurer")
);
const PremiumAndClaims = lazy(
  () => import("@/pages/re-insurance/treaty/premium-and-claims")
);
const ClaimDetails = lazy(
  () => import("@/pages/re-insurance/treaty/premium-and-claims/claim-details")
);
const PremiumDetail = lazy(
  () => import("@/pages/re-insurance/treaty/premium-and-claims/premium-detail")
);
const Facultative = lazy(() => import("@/pages/re-insurance/facultative"));
const FacultativeDetails = lazy(
  () => import("@/pages/re-insurance/facultative/facultative-details")
);

const Login = lazy(() => import("../pages/login"));
const NotFound = lazy(() => import("../pages/404"));

export function Routes() {
  return (
    <RouterRoutes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      <Route path="/" element={<PrivateRoute />}>
        <Route element={<ProtectedLayout />}>
          <Route index element={<ReinsuranceHomeRedirect />} />
          <Route
            path="re-insurance/treaty"
            element={
              <RequireFeature
                feature={FEATURE.TREATY}
                permission={PERMISSION.VIEW_TREATIES}
              >
                <Treaty />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/add-treaty"
            element={
              <RequireFeature
                feature={FEATURE.TREATY}
                permission={PERMISSION.CREATE_TREATY}
              >
                <AddTreaty />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/treaty-details/:id"
            element={
              <RequireFeature
                feature={FEATURE.TREATY}
                permission={PERMISSION.VIEW_TREATIES}
              >
                <TreatyDetails />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/treaty-details/:id/renew"
            element={
              <RequireFeature
                feature={FEATURE.TREATY}
                permission={PERMISSION.RENEW_TREATY}
              >
                <RenewTreaty />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/treaty-details/:id/rules"
            element={<RedirectTreatyRulesToDetails />}
          />
          <Route
            path="re-insurance/treaty-details/:id/premium-and-claims"
            element={<PremiumAndClaims />}
          />
          <Route
            path="re-insurance/treaty-details/:id/premium-and-claims/claim/:claimId"
            element={<ClaimDetails />}
          />
          <Route
            path="re-insurance/treaty-details/:id/premium-and-claims/premium/:clientAccountId"
            element={<PremiumDetail />}
          />
          <Route
            path="re-insurance/brokers"
            element={
              <RequireFeature
                feature={FEATURE.BROKER}
                permission={PERMISSION.VIEW_BROKERS}
              >
                <Brokers />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/add-broker"
            element={
              <RequireFeature
                feature={FEATURE.BROKER}
                permission={PERMISSION.ADD_BROKER}
              >
                <AddBroker />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/broker-details/:id"
            element={
              <RequireFeature
                feature={FEATURE.BROKER}
                permission={PERMISSION.VIEW_BROKERS}
              >
                <BrokerDetails />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/broker-details/:id/edit"
            element={
              <RequireFeature
                feature={FEATURE.BROKER}
                permission={PERMISSION.EDIT_BROKER}
              >
                <EditBroker />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/reinsurers"
            element={
              <RequireFeature
                feature={FEATURE.REINSURER}
                permission={PERMISSION.VIEW_REINSURERS}
              >
                <Reinsurers />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/add-reinsurer"
            element={
              <RequireFeature
                feature={FEATURE.REINSURER}
                permission={PERMISSION.ADD_REINSURER}
              >
                <AddReinsurer />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/reinsurer-details/:id"
            element={
              <RequireFeature
                feature={FEATURE.REINSURER}
                permission={PERMISSION.VIEW_REINSURERS}
              >
                <ReinsurerDetails />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/reinsurer-details/:id/edit"
            element={
              <RequireFeature
                feature={FEATURE.REINSURER}
                permission={PERMISSION.EDIT_REINSURER}
              >
                <EditReinsurer />
              </RequireFeature>
            }
          />
          <Route
            path="re-insurance/reinsurer-details/:id/statements"
            element={
              <RequireFeature
                feature={FEATURE.REINSURER}
                permission={PERMISSION.VIEW_REINSURERS}
              >
                <StatementsOverview />
              </RequireFeature>
            }
          />
          <Route path="re-insurance/compliance" element={<Compliance />} />
          <Route path="re-insurance/facultative" element={<Facultative />} />
          <Route
            path="re-insurance/facultative/:id"
            element={<FacultativeDetails />}
          />
          <Route path="re-insurance/policy" element={<Policy />} />
          <Route path="re-insurance/policy/:id" element={<PolicyDetails />} />
          <Route
            path="re-insurance/compliance/treaty-details/:id"
            element={<ComplianceTreatyDetails />}
          />
          <Route
            path="re-insurance/compliance/broker-details/:id"
            element={<ComplianceBrokerDetails />}
          />
          <Route
            path="re-insurance/compliance/reinsurer-details/:id"
            element={<ComplianceReinsurerDetails />}
          />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
}
